import React, { useEffect, useState } from 'react';

import { RoyaltyModelsEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { CellEditRequestEvent, CsvExportParams, EditableCallbackParams, GridApi } from 'ag-grid-community';
import { cloneDeep, find } from 'lodash';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { FilterInput } from 'src/components/FilterInput';
import { CorePlan, RateCardCoreRateGridColumnName, RateMultiplierDisplayName } from 'src/constants';
import { CORE_PLAN_COLUMN_TO_RATE_TYPE_MAP, MULTIPLIER_NAME_MAP } from 'src/constants/rateCards';
import { IFirstPartyRateGridRowData, IRateCardDetailStepProps } from 'src/types/IRateCard';
import { getDerivedMultiplierName } from 'src/util/RateCards';
import { getCurrentDate } from 'src/util/dateTimeUtil';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getCustomMultiplierCellStyle } from 'src/util/grid/cellStyle';
import { validateRequired } from 'src/util/validationsUtil';

import { commonColDef, firstPartyRatesColDefs } from './firstPartyRatesColDefs';

export const FirstPartyRates: React.FC<IRateCardDetailStepProps> = ({
    rateCardValues,
    setRateCardValues,
    isEditMode,
    multiplierTypeList,
    corePlanList,
    setIsNextDisabled,
    setIsPrevDisabled,
    dataLoaded
}: IRateCardDetailStepProps) => {
    const [filterText, setFilterText] = useState('');
    const [agGridApi, setAgGridApi] = useState<GridApi>();

    // When in edit mode, filter out the (HD) 90D FT rates if they are included in core rates
    useEffect(() => {
        if (!isEditMode) {
            return;
        }

        const has90DayFtCoreRates = rateCardValues.coreRates.some((rate) =>
            validateRequired(rate[RateCardCoreRateGridColumnName.NinetyDayFTCPP]?.toString())
        );
        const hasHd90DayFtCoreRates = rateCardValues.coreRates.some((rate) =>
            validateRequired(rate[RateCardCoreRateGridColumnName.HDNinetyDayFTCPP]?.toString())
        );

        // If 90D FT is not in core rates, include everything
        if (!has90DayFtCoreRates) {
            return;
        }

        const filteredFirstPartyRates = rateCardValues.firstPartyRates.filter(
            (row) =>
                row.paperPlanName !== CorePlan.HAWKFIRE_ALL_DEVICES_90DAY_FREE_TRIAL &&
                !(hasHd90DayFtCoreRates && row.paperPlanName === CorePlan.HAWKFIRE_KATANA_IND_MONTHLY_90_DAY_FT)
        );

        setRateCardValues((prevState) => ({
            ...prevState,
            firstPartyRates: filteredFirstPartyRates
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateFirstPartyRates = () => {
        if (!setIsNextDisabled || !setIsPrevDisabled) {
            return;
        }

        const isButtonDisabled = rateCardValues.firstPartyRates.some(
            (row) => row.multiplierName === RateMultiplierDisplayName.CUSTOM && !validateRequired(row.multiplierValue)
        );
        setIsPrevDisabled(isButtonDisabled);
        setIsNextDisabled(isButtonDisabled);
    };

    const exportFirstPartyDiscountRates = () => {
        const params: CsvExportParams = {
            fileName: `1p_discount_rates_export_${getCurrentDate()}.csv`
        };
        agGridApi?.exportDataAsCsv(params);
    };

    const colDefs = [
        ...firstPartyRatesColDefs,
        {
            headerName: 'Rate card inputs',
            children: [
                {
                    headerName: 'Core plan',
                    field: 'corePlan',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: isEditMode,
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: { values: corePlanList?.map((t) => t.corePlanDisplayName) },
                    ...commonColDef
                },
                {
                    headerName: 'Rate type',
                    field: 'rateType',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: false,
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: { values: [RoyaltyModelsEnum.CPP, RoyaltyModelsEnum.PSM] },
                    ...commonColDef
                },
                {
                    headerName: 'Multiplier name',
                    field: 'multiplierName',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: isEditMode,
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        values: multiplierTypeList
                            ?.filter((t) => MULTIPLIER_NAME_MAP.has(t.name || ''))
                            .map((t) => t.displayName)
                    },
                    ...commonColDef
                },
                {
                    headerName: 'Multiplier value',
                    field: 'multiplierValue',
                    filter: 'agNumberColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: (params: EditableCallbackParams) => {
                        return params.data.multiplierName === RateMultiplierDisplayName.CUSTOM && isEditMode;
                    },
                    ...commonColDef,
                    cellStyle: getCustomMultiplierCellStyle
                }
            ]
        }
    ];

    function updateMultiplier(
        newRateType: string | undefined,
        rowData: IFirstPartyRateGridRowData
    ): IFirstPartyRateGridRowData {
        if (newRateType === RoyaltyModelsEnum.CPP) {
            // update multiplier name to be No Multiplier and multiplier value to 1

            return {
                ...rowData,
                rateType: newRateType,
                multiplierName: RateMultiplierDisplayName.NONE,
                multiplierTypeId: find(multiplierTypeList, { displayName: RateMultiplierDisplayName.NONE })
                    ?.rateMultiplierTypeId,
                multiplierValue: '1'
            };
        } else if (newRateType === RoyaltyModelsEnum.PSM) {
            // trigger auto populating multiplier when choose PSM as rate type

            //@ts-ignore
            const derivedMultiplierName = getDerivedMultiplierName(rowData);
            return {
                ...rowData,
                rateType: newRateType,
                multiplierName: derivedMultiplierName || '',
                multiplierTypeId: find(multiplierTypeList, { displayName: derivedMultiplierName })
                    ?.rateMultiplierTypeId,
                multiplierValue:
                    //@ts-ignore
                    rateCardValues.contractTerms.rateMultipliers[
                        find(multiplierTypeList, { displayName: derivedMultiplierName })?.contractTermMultiplierKey ||
                            ''
                    ]
            };
        } else {
            return rowData;
        }
    }

    const onCellEditRequest = (event: CellEditRequestEvent<any>) => {
        const { column, newValue, node } = event;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const rowId = Number(node.id!);

        if (rowId === null || rowId === undefined) return;

        const newRowData = cloneDeep(rateCardValues.firstPartyRates);

        if (column.getColId() === 'multiplierName') {
            newRowData[rowId] = {
                ...newRowData[rowId],
                multiplierName: newValue,
                multiplierTypeId: find(multiplierTypeList, { displayName: newValue })?.rateMultiplierTypeId,
                multiplierValue:
                    //@ts-ignore
                    rateCardValues.contractTerms.rateMultipliers[
                        find(multiplierTypeList, { displayName: newValue })?.contractTermMultiplierKey || ''
                    ]
            };
        } else if (column.getColId() === 'corePlan') {
            newRowData[rowId] = {
                ...newRowData[rowId],
                corePlan: newValue,
                corePlanId: find(corePlanList, { corePlanDisplayName: newValue })?.corePlanId
            };
            const newRateType = CORE_PLAN_COLUMN_TO_RATE_TYPE_MAP.get(newValue);
            newRowData[rowId] = updateMultiplier(newRateType, newRowData[rowId]);
        } else if (column.getColId() === 'rateType') {
            newRowData[rowId] = updateMultiplier(newValue, newRowData[rowId]);
        } else {
            newRowData[rowId] = {
                ...newRowData[rowId],
                [column.getColId()]: newValue
            };
        }

        setRateCardValues((prevState) => {
            return {
                ...prevState,
                firstPartyRates: newRowData
            };
        });

        validateFirstPartyRates();
    };

    return (
        <Column height="100%" width="100%" minHeight={480} spacing="300">
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <Button type="link" size="small" onClick={exportFirstPartyDiscountRates}>
                        <Icon tokens={downloadLargeTokens} />
                        Download
                    </Button>
                </Row>
            </Box>
            <BaseAgGrid
                columnDefs={colDefs}
                rowData={rateCardValues.firstPartyRates}
                filterText={filterText}
                setAgGridApi={setAgGridApi}
                suppressRowClickSelection={true}
                singleClickEdit={false}
                readOnlyEdit={true}
                onCellEditRequest={onCellEditRequest}
                suppressScrollOnNewData={true}
                loading={!dataLoaded}
            />
        </Column>
    );
};
